import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AGENCY_TZ, api, formatDateTime } from "../api";
import { useApi } from "../hooks";
import { RequestView } from "./Workflow";

type Kind = "team" | "task" | "data" | "sync";
type Item = {
  at: string;
  kind: Kind;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  title: string;
  detail: string | null;
  /** The person who did it ("by Neil Busque"). */
  actor: string | null;
  /** The system that did it ("Nightly sync", "Drop link"). */
  via?: string | null;
  /** Where the line opens (a task, a report), when it has a page of its own. */
  link?: string | null;
  failed?: boolean;
};
type Page = { items: Item[]; has_more: boolean; next_before?: string | null };

const FILTERS: { value: Kind | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "team", label: "Team" },
  { value: "task", label: "Tasks" },
  { value: "data", label: "Data" },
  { value: "sync", label: "Syncs" },
];

// Days and times on the agency's clock, whoever is looking.
const dayKey = (d: Date) => new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: AGENCY_TZ }).format(d);
/** "6:00 AM": the row's time; its day is the heading above it. */
const timeOf = (iso: string) => new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: AGENCY_TZ }).format(new Date(iso));
/** Group rows by the agency's calendar day so a long list reads like a diary. */
function dayOf(iso: string) {
  const d = new Date(iso);
  const key = dayKey(d);
  const now = new Date();
  if (key === dayKey(now)) return "Today";
  if (key === dayKey(new Date(now.getTime() - 86_400_000))) return "Yesterday";
  const thisYear = key.slice(0, 4) === dayKey(now).slice(0, 4);
  return new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", ...(thisYear ? {} : { year: "numeric" }), timeZone: AGENCY_TZ }).format(d);
}

/**
 * Everything that happened to one client, newest first: what the team did
 * (edits, notes, numbers, connects), tasks opened and closed, job exports and
 * conversion sends, provider syncs. Filter by kind; load older in pages.
 */
export default function ActivityLog({ clientId, refresh = 0 }: { clientId: number; refresh?: number }) {
  const [kind, setKind] = useState<Kind | "all">("all");
  const path = `/api/clients/${clientId}/activity?limit=10${kind === "all" ? "" : `&kind=${kind}`}`;
  const first = useApi<Page>(path);
  // The profile bumps `refresh` after any save, so the new line shows at once.
  useEffect(() => { if (refresh) first.reload(); }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  const [older, setOlder] = useState<Item[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => { setOlder([]); setHasMore(false); setCursor(null); }, [path]);
  useEffect(() => {
    if (first.data) { setHasMore(first.data.has_more); setCursor(first.data.next_before ?? null); }
  }, [first.data]);

  const items = [...(first.data?.items || []), ...older];
  const loadMore = async () => {
    // The server says where the next page starts, so a folded sync run is never shown twice.
    const before = cursor || items[items.length - 1]?.at;
    if (!before) return;
    setLoadingMore(true);
    try {
      const page = await api<Page>(`${path}&before=${encodeURIComponent(before)}`);
      setOlder((o) => [...o, ...page.items]);
      setHasMore(page.has_more);
      setCursor(page.next_before ?? null);
    } catch { setHasMore(false); }
    finally { setLoadingMore(false); }
  };

  let lastDay = "";
  return (
    <section className="panel detail-wide activity-panel" aria-labelledby="activity-title">
      <div className="panel-heading">
        <div>
          <h2 id="activity-title">Activity</h2>
        </div>
        <div className="tabs activity-filters" role="tablist" aria-label="Activity filter">
          {FILTERS.map((f) => (
            <button key={f.value} role="tab" aria-selected={kind === f.value} className={kind === f.value ? "active" : ""} onClick={() => setKind(f.value)}>{f.label}</button>
          ))}
        </div>
      </div>
      <RequestView request={first}>
        {items.length ? (
          <ol className="activity-list">
            {items.map((it, i) => {
              const day = dayOf(it.at);
              const showDay = day !== lastDay;
              lastDay = day;
              const who = it.actor ? `by ${it.actor}` : it.via || null;
              return (
                <li key={`${it.kind}-${it.entity_type}-${it.entity_id ?? i}-${it.at}`} className={[showDay ? "activity-day-start" : "", it.link ? "has-link" : ""].filter(Boolean).join(" ") || undefined}>
                  {showDay && <div className="activity-day">{day}</div>}
                  <div className="activity-row">
                    <time dateTime={it.at} title={formatDateTime(it.at)}>{timeOf(it.at)}</time>
                    <div className="activity-body">
                      <p className={it.failed ? "activity-title is-failed" : "activity-title"}>
                        {it.link ? <Link to={it.link}>{it.title}</Link> : it.title}
                      </p>
                      {(who || it.detail) && (
                        <p className="activity-meta">
                          {it.detail ? <span className="activity-detail">{it.detail}</span> : null}
                          {who ? <span className="activity-who">{who}</span> : null}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="workflow-note">
            {kind === "all"
              ? "Nothing recorded for this client yet. Edits, logged contacts, tasks, job exports, conversion sends and failed syncs show up here as they happen."
              : `No ${FILTERS.find((f) => f.value === kind)?.label.toLowerCase()} activity yet.`}
          </p>
        )}
        {hasMore && (
          <div className="activity-more">
            <button className="button secondary" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? "Loading…" : "Show more"}</button>
          </div>
        )}
      </RequestView>
    </section>
  );
}
